import { FrameStencil, HandoffAction, MarkerPlayer, PlayerAction, Point, PossessionAction } from "../../types";
import { getActionPath, getPath } from "../get-action-path";
import { getDistanceBetweenPoints } from "../get-distance-between-points";

export const DEFAULT_ACTION_RANGE = 6;
export const DEFAULT_HANDOFF_ACTION_RANGE = 6;
export const INTERSECTION_RESOLUTION = 0.5;

export interface ComparisonPoint {
  id: string;
  point: Point;
}

export interface GetClosestPointProps {
  point: ComparisonPoint;
  comparisonPoints: ComparisonPoint[];
  actionRange?: number;
}

export const findClosestPoint = ({
  point,
  comparisonPoints,
  actionRange = DEFAULT_ACTION_RANGE,
}: GetClosestPointProps): ComparisonPoint | null => {
  const comparisonPointsSorted = [
    ...comparisonPoints
      .filter(({ id }) => id !== point.id)
      .map((comparisonPoint) => ({
        ...comparisonPoint,
        distance: getDistanceBetweenPoints({
          x1: comparisonPoint.point.x,
          y1: comparisonPoint.point.y,
          x2: point.point.x,
          y2: point.point.y,
        }),
      }))
      .filter(({ distance }) => distance <= actionRange),
  ].sort((a, b) => a.distance - b.distance);

  if (comparisonPointsSorted[0]) {
    const { distance: _distance, ...restComparisonPoint } = comparisonPointsSorted[0];
    return restComparisonPoint;
  } else return null;
};

export interface GetClosestStencilToPointProps<TStencil extends FrameStencil> {
  target: { id: string; point: Point };
  stencils: TStencil[];
  stencilsPointAccessor?: (stencil: TStencil) => Point;
  actionRange?: number;
}

export const findClosestStencil = <TStencil extends FrameStencil>({
  target,
  stencils,
  stencilsPointAccessor = (s: FrameStencil) => s.pos,
  actionRange = DEFAULT_ACTION_RANGE,
}: GetClosestStencilToPointProps<TStencil>): TStencil | null => {
  const closestPointToStartPoint = findClosestPoint({
    point: target,
    comparisonPoints: stencils.map((s) => ({ id: s.id, point: stencilsPointAccessor(s) })),
    actionRange,
  });

  const closestStencilToStartPoint = stencils.find((s) => s.id === closestPointToStartPoint?.id);

  return closestStencilToStartPoint || null;
};

export interface FindClosestPathProps<TPath extends PossessionAction | PlayerAction> {
  target: Point;
  stencils: TPath[];
}

export interface ActionWithIntersection<TPath extends PossessionAction | PlayerAction | HandoffAction> {
  action: TPath;
  intersection: Point;
  pathPercentage: number;
  distance: number;
}

export const findClosestPath = <TPath extends PossessionAction | PlayerAction>({
  target,
  stencils,
}: FindClosestPathProps<TPath>): ActionWithIntersection<TPath> | null => {
  const stencilPoints: ActionWithIntersection<TPath>[] = stencils
    .map((s): ActionWithIntersection<TPath> | null => {
      const path = getPath(getActionPath(s));
      const pathLength = path.getTotalLength();
      const pointsToCheck = Math.floor(pathLength / INTERSECTION_RESOLUTION);
      const intersectionPoints = Array(pointsToCheck + 1)
        .fill(0)
        .map((_, i) => {
          const pathPercentage = i / pointsToCheck;
          const intersection = path.getPointAtLength(pathPercentage * pathLength);

          return { pathPercentage, intersection: { x: intersection.x, y: intersection.y } };
        })
        .map(({ pathPercentage, intersection }) => ({
          ...intersection,
          pathPercentage,
          distance: getDistanceBetweenPoints({ x1: intersection.x, y1: intersection.y, x2: target.x, y2: target.y }),
        }))
        .filter(({ distance }) => distance <= DEFAULT_HANDOFF_ACTION_RANGE)
        .sort((a, b) => a.distance - b.distance);

      if (intersectionPoints[0]) {
        return {
          action: s,
          distance: intersectionPoints[0].distance,
          pathPercentage: intersectionPoints[0].pathPercentage,
          intersection: { x: intersectionPoints[0].x, y: intersectionPoints[0].y },
        };
      } else return null;
    })
    .filter((s): s is ActionWithIntersection<TPath> => s !== null);

  const stencilPointsSorted: ActionWithIntersection<TPath>[] = stencilPoints.sort((a, b) => a.distance - b.distance);
  if (stencilPointsSorted[0]) return stencilPointsSorted[0];
  else return null;
};

export interface FindClosestToHandoffProps<TPath extends PossessionAction | PlayerAction> {
  target: Point;
  pathStencils: TPath[];
  playerStencils: MarkerPlayer[];
}

export enum ClosestToHandoffType {
  PATH = "PATH",
  PLAYER = "PLAYER",
}
export type ClosestPathToHandoff<TPath extends PossessionAction | PlayerAction> = {
  type: ClosestToHandoffType.PATH;
  actionWithIntersection: ActionWithIntersection<TPath>;
};

export type ClosestPlayerToHandoff = {
  type: ClosestToHandoffType.PLAYER;
  player: MarkerPlayer;
};

export const findClosestToHandoff = <TPath extends PossessionAction | PlayerAction>({
  target,
  pathStencils,
  playerStencils,
}: FindClosestToHandoffProps<TPath>): ClosestPathToHandoff<TPath> | ClosestPlayerToHandoff | null => {
  const closestPath = findClosestPath({ target, stencils: pathStencils });
  const closestPlayer = findClosestStencil({
    target: { id: "target", point: target },
    stencils: playerStencils,
    actionRange: DEFAULT_HANDOFF_ACTION_RANGE,
  });

  if (closestPath && closestPlayer) {
    const playerDistance = getDistanceBetweenPoints({
      x1: closestPlayer.pos.x,
      y1: closestPlayer.pos.y,
      x2: target.x,
      y2: target.y,
    });
    const pathDistance = closestPath.distance;

    if (playerDistance <= pathDistance) return { player: closestPlayer, type: ClosestToHandoffType.PLAYER };
    else return { actionWithIntersection: closestPath, type: ClosestToHandoffType.PATH };
  } else if (closestPath) return { actionWithIntersection: closestPath, type: ClosestToHandoffType.PATH };
  else if (closestPlayer) return { player: closestPlayer, type: ClosestToHandoffType.PLAYER };
  else return null;
};

export interface FindClosestHandoffProps {
  stencils: HandoffAction[];
  target: PossessionAction | PlayerAction;
}

export const findClosestHandoff = ({ stencils, target }: FindClosestHandoffProps) => {
  const path = getPath(getActionPath(target));
  const pathLength = path.getTotalLength();
  const pointsToCheck = Math.floor(pathLength / INTERSECTION_RESOLUTION);
  const intersectionPoints = Array(pointsToCheck + 1)
    .fill(0)
    .map((_, i) => {
      const pathPercentage = i / pointsToCheck;
      const intersection = path.getPointAtLength(pathPercentage * pathLength);

      return { pathPercentage, intersection: { x: intersection.x, y: intersection.y } };
    });

  const handoffDistances = stencils
    .map((s) => {
      const pointsSorted = intersectionPoints
        .map(({ pathPercentage, intersection }) => ({
          ...intersection,
          pathPercentage,
          distance: getDistanceBetweenPoints({ x1: intersection.x, y1: intersection.y, x2: s.pos.x, y2: s.pos.y }),
        }))
        .filter(({ distance }) => distance <= DEFAULT_HANDOFF_ACTION_RANGE)
        .sort((a, b) => a.distance - b.distance);

      if (pointsSorted[0]) {
        return {
          action: s,
          distance: pointsSorted[0].distance,
          pathPercentage: pointsSorted[0].pathPercentage,
          intersection: { x: pointsSorted[0].x, y: pointsSorted[0].y },
        };
      } else return null;
    })
    .filter((s): s is ActionWithIntersection<HandoffAction> => s !== null)
    .sort((a, b) => a.distance - b.distance);

  if (handoffDistances[0]) return handoffDistances[0];
  else return null;
};
